import { useEffect, useState } from 'react'
import { useFlag } from '@openfeature/react-sdk'
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import {
  selectFirstErrorMessageForKeys,
  selectIsFetchingForKeys,
  invalidateFetch,
} from '@/reducers/fetch'
import {
  loginUser,
  POST_LOGIN_KEY,
  POST_LOGIN_SSO_KEY,
  loginUserSso,
} from '@/actions/authActions'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  useAnalyticsSetUserId,
  useAnalyticsTrack,
  useAnalyticsView,
} from '@/features/amplitude'
import { useZendeskSso, RETURN_TO_KEY } from '@/features/zendesk/helpers'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { SIGNUP_PATHS } from '@/features/user/signup/helpers'
import { MaintenanceBanner } from '@/components/platform-banners/MaintenanceBanner'
import { OTPLogin } from './OTPLogin'
import { TypographyStyles } from '@/styles'
import { Button, Input, Image, Text, Alert, Card, Dialog } from '@/ui'

export const Login = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState<string>()
  const [showOtp, setShowOtp] = useState(false)
  const [phoneDigits, setPhoneDigits] = useState('')
  const [userHasEmailVerified, setUserHasEmailVerified] = useState(false)
  const auth = useReselector((state) => state.auth.security)
  const { pathname } = useLocation()
  const { zendeskForm, submitForm } = useZendeskSso({
    userHasEmailVerified,
  })
  const [searchParams] = useSearchParams()

  const shouldUseJwt = pathname.endsWith('zendesk/sso')
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()
  const setUserId = useAnalyticsSetUserId()
  const track = useAnalyticsTrack()
  const navigate = useNavigate()

  const loginError = useReselector(selectFirstErrorMessageForKeys, [
    POST_LOGIN_KEY,
    POST_LOGIN_SSO_KEY,
  ])

  const loginSubmitting = useReselector(selectIsFetchingForKeys, [
    POST_LOGIN_KEY,
    POST_LOGIN_SSO_KEY,
  ])

  const maintenanceMessage = useFlag(FEATURE_FLAG_KEYS.maintenanceMessage, {
    message: null,
    pages: [] as string[],
  })

  useEffect(() => {
    // This refreshes the page which will update the index.html file.  The index.html file is not cached and will
    // reference the js bundle.  If that name has changed (hash is appended) then the user will receive updated code
    if (!window.location.hash) {
      window.location.hash = 'loaded'
      window.location.reload()
      return
    }
    pageView('login')
  }, [pageView])

  useEffect(() => {
    if (loginError)
      track('attempted login to Heard app', {
        status: 'unsuccessful',
      })
  }, [loginError, track])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const targetLink = localStorage.getItem('targetLink')

    const payload = {
      email: email.toLowerCase(),
      password,
      otp,
    }

    const result = shouldUseJwt
      ? await loginUserSso(payload)(dispatch)
      : await loginUser(payload)(dispatch)

    if (!result) return

    // If this is in result should nav user to zendesk url
    if ('jwt' in result.data) {
      track('attempted login to Heard app with zendesk sso', {
        status: 'successful',
      })

      if (
        'emailVerified' in result.data &&
        Boolean(result.data.emailVerified)
      ) {
        setUserHasEmailVerified(true)
        localStorage.setItem(
          `original_${RETURN_TO_KEY}`,
          searchParams.get(RETURN_TO_KEY) || ''
        )
      }

      submitForm(result.data.jwt)
    } else if (!('lastDigits' in result.data)) {
      // Successful login
      // Sets the amplitude user id
      // Note: We set this property within login form instead of PracticeProfile/index.tsx
      // b/c it ensures actual customers have logged in, not god-viewed users (Heard staff impersonating customers)
      setUserId(result.data.uuid)

      track('attempted login to Heard app', {
        status: 'successful',
      })

      if (targetLink) navigate(targetLink)
    } else {
      // OTP must be entered
      setShowOtp(true)
      setPhoneDigits(result.data.lastDigits)
    }
  }

  if (auth.isAuthenticated) {
    if (shouldUseJwt) {
      return <Navigate to="/accounts/zendesk/sso" />
    }

    return <Navigate to="/dashboard" />
  }

  const handleOtpClose = () => {
    setShowOtp(false)
    dispatch(invalidateFetch(POST_LOGIN_KEY))
    dispatch(invalidateFetch(POST_LOGIN_SSO_KEY))
  }

  return (
    <div className="finances login w-full background-app h-full">
      {(maintenanceMessage?.value?.pages ?? []).includes('login') && (
        <MaintenanceBanner />
      )}

      <div className="w-full text-center pt-12 flex flex-col items-center space-y-6">
        <div className="flex justify-center">
          <Image
            src="https://heard-images.s3.us-west-1.amazonaws.com/heard_logo_green_2x.png"
            alt="heard logo"
            height={20}
            width={100}
          />
        </div>

        <Text variant={TypographyStyles.H2}>Welcome back!</Text>

        <div className="min-h-fit">
          <Text textAlign="center">
            Don&apos;t have an account?{' '}
            <Link
              to={SIGNUP_PATHS.emailCapture}
              className="inline text-brand underline"
            >
              Sign up here.
            </Link>
          </Text>
        </div>

        <div className="flex justify-center w-full">
          <Card className="w-full md:w-3/4 lg:w-1/2">
            {loginError && !showOtp && (
              <Alert variant="error" title={loginError} />
            )}

            <form
              onSubmit={handleSubmit}
              className={loginError && !showOtp ? 'mt-4' : ''}
            >
              <div className="flex flex-col space-y-4">
                {auth.updatedPassword && (
                  <Alert title="You've successfully changed your password! Please login with your new credentials." />
                )}

                <Input
                  placeholder="Email"
                  name="email"
                  type="email"
                  onChange={(e) => setEmail(e.target.value)}
                />

                <Input
                  placeholder="Password"
                  name="password"
                  type="password"
                  onChange={(e) => setPassword(e.target.value)}
                />

                <Button
                  type="submit"
                  loading={loginSubmitting}
                  disabled={loginSubmitting}
                >
                  Login
                </Button>
              </div>
            </form>
          </Card>
        </div>

        <Link to="/recover" className="underline text-brand">
          Forgot password
        </Link>

        <Dialog open={showOtp} onClose={handleOtpClose}>
          <Dialog.Content onDismiss={handleOtpClose}>
            <OTPLogin
              email={email}
              onSubmit={handleSubmit}
              loginError={loginError}
              otp={otp}
              setOtp={setOtp}
              password={password}
              phoneDigits={phoneDigits}
              onClose={handleOtpClose}
            />
          </Dialog.Content>
        </Dialog>

        {zendeskForm}
      </div>
    </div>
  )
}
